/**
 * Font Registry — maps Quill font IDs to pdfmake font configuration.
 *
 * Each entry maps a Quill CSS class suffix (e.g. `ql-font-georgia` → `'georgia'`)
 * to the pdfmake font name and local TTF file paths.
 *
 * TTF files are stored in `/public/fonts/ttf/` and served statically. This avoids
 * runtime network requests and CFF/WOFF compatibility issues with pdfmake.
 *
 * Standard PDF fonts (Helvetica, Courier, Times) use pdfmake's built-in AFM modules
 * and don't need TTF files.
 */

export interface FontVariants {
    normal: string;
    bold: string;
    italics: string;
    bolditalics: string;
}

export interface FontRegistryEntry {
    /** Quill class suffix, e.g. 'georgia' from ql-font-georgia */
    quillId: string;
    /** Font family name for pdfmake content, e.g. 'Georgia' */
    pdfmakeName: string;
    /** Where the font data comes from */
    source: 'standard' | 'local';
    /** pdfmake standard-font module name (standard fonts only) */
    standardFont?: 'Courier' | 'Helvetica' | 'Times';
    /** TTF file paths relative to /fonts/ttf/ (local fonts only) */
    ttfFiles?: FontVariants;
}

/** Build a FontVariants object, falling back to the regular file for missing variants */
function variants(
    normal: string,
    bold?: string,
    italics?: string,
    bolditalics?: string,
): FontVariants {
    return {
        normal,
        bold: bold || normal,
        italics: italics || normal,
        bolditalics: bolditalics || bold || italics || normal,
    };
}

/**
 * All Quill editor fonts mapped to their pdfmake configuration.
 *
 * Font IDs are generated by TextArea.tsx's `getFontId()`:
 *   entry.split(',')[0].replace(/\s+/g, '').toLowerCase()
 */
export const FONT_REGISTRY = new Map<string, FontRegistryEntry>([

    // ── Standard PDF fonts (built into PDF readers, no TTF needed) ────────

    ['arial', {
        quillId: 'arial',
        pdfmakeName: 'Helvetica',
        source: 'standard',
        standardFont: 'Helvetica',
    }],
    ['helvetica', {
        quillId: 'helvetica',
        pdfmakeName: 'Helvetica',
        source: 'standard',
        standardFont: 'Helvetica',
    }],

    // ── Local TTF fonts ──────────────────────────────────────────────────

    ['andalemono', {
        quillId: 'andalemono',
        pdfmakeName: 'AndaleMono',
        source: 'local',
        ttfFiles: variants('AndaleMono-400-normal.ttf'),
    }],
    ['calibri', {
        quillId: 'calibri',
        pdfmakeName: 'Calibri',
        source: 'local',
        ttfFiles: variants(
            'Calibri-400-normal.ttf',
            'Calibri-700-normal.ttf',
            'Calibri-400-italic.ttf',
            'Calibri-700-italic.ttf',
        ),
    }],
    ['comicsansms', {
        quillId: 'comicsansms',
        pdfmakeName: 'ComicSans',
        source: 'local',
        ttfFiles: variants(
            'ComicSans-400-normal.ttf',
            'ComicSans-700-normal.ttf',
            'ComicSans-400-italic.ttf',
            'ComicSans-700-italic.ttf',
        ),
    }],
    ['couriernew', {
        quillId: 'couriernew',
        pdfmakeName: 'CourierNew',
        source: 'local',
        ttfFiles: variants('CourierNew-400-normal.ttf'),
    }],
    ['frederikathegreat', {
        quillId: 'frederikathegreat',
        pdfmakeName: 'FrederickaTheGreat',
        source: 'local',
        ttfFiles: variants('FrederickaTheGreat-400-normal.ttf'),
    }],
    ['georgia', {
        quillId: 'georgia',
        pdfmakeName: 'Georgia',
        source: 'local',
        ttfFiles: variants(
            'Georgia-400-normal.ttf',
            undefined,
            'Georgia-400-italic.ttf',
        ),
    }],
    ['impact', {
        quillId: 'impact',
        pdfmakeName: 'Impact',
        source: 'local',
        ttfFiles: variants('Impact-400-normal.ttf'),
    }],
    ['inconsolata', {
        quillId: 'inconsolata',
        pdfmakeName: 'Inconsolata',
        source: 'local',
        ttfFiles: variants('Inconsolata-400-normal.ttf'),
    }],
    ['lucidaconsole', {
        quillId: 'lucidaconsole',
        pdfmakeName: 'LucidaConsole',
        source: 'local',
        ttfFiles: variants('LucidaConsole-400-normal.ttf'),
    }],
    ['lucidasans', {
        quillId: 'lucidasans',
        pdfmakeName: 'LucidaSans',
        source: 'local',
        ttfFiles: variants(
            'LucidaSans-400-normal.ttf',
            'LucidaSans-600-normal.ttf',
            'LucidaSans-400-italic.ttf',
            'LucidaSans-600-italic.ttf',
        ),
    }],
    ['nunito', {
        quillId: 'nunito',
        pdfmakeName: 'NunitoSans',
        source: 'local',
        ttfFiles: variants(
            'NunitoSans-400-normal.ttf',
            'NunitoSans-400-normal.ttf',
            'NunitoSans-400-italic.ttf',
            'NunitoSans-400-italic.ttf',
        ),
    }],
    ['opensans', {
        quillId: 'opensans',
        pdfmakeName: 'OpenSans',
        source: 'local',
        ttfFiles: variants(
            'OpenSans-400-normal.ttf',
            'OpenSans-400-normal.ttf',
            'OpenSans-400-italic.ttf',
            'OpenSans-400-italic.ttf',
        ),
    }],
    ['palatinolinotype', {
        quillId: 'palatinolinotype',
        pdfmakeName: 'PalatinoLinotype',
        source: 'local',
        ttfFiles: variants('PalatinoLinotype-400-normal.ttf'),
    }],
    ['poppins', {
        quillId: 'poppins',
        pdfmakeName: 'Poppins',
        source: 'local',
        ttfFiles: variants(
            'Poppins-400-normal.ttf',
            'Poppins-700-normal.ttf',
            'Poppins-400-italic.ttf',
            'Poppins-700-italic.ttf',
        ),
    }],
    ['spectralsc', {
        quillId: 'spectralsc',
        pdfmakeName: 'SpectralSC',
        source: 'local',
        ttfFiles: variants(
            'SpectralSC-400-normal.ttf',
            'SpectralSC-700-normal.ttf',
            'SpectralSC-400-italic.ttf',
            'SpectralSC-700-italic.ttf',
        ),
    }],
    ['tahoma', {
        quillId: 'tahoma',
        pdfmakeName: 'Tahoma',
        source: 'local',
        ttfFiles: variants('Tahoma-400-normal.ttf'),
    }],
    ['timesnewroman', {
        quillId: 'timesnewroman',
        pdfmakeName: 'TimesNewRoman',
        source: 'local',
        ttfFiles: variants('TimesNewRoman-400-normal.ttf'),
    }],
    ['trebuchetms', {
        quillId: 'trebuchetms',
        pdfmakeName: 'TrebuchetMS',
        source: 'local',
        ttfFiles: variants(
            'TrebuchetMS-400-normal.ttf',
            'TrebuchetMS-400-normal.ttf',
            'TrebuchetMS-400-italic.ttf',
            'TrebuchetMS-400-italic.ttf',
        ),
    }],
    ['verdana', {
        quillId: 'verdana',
        pdfmakeName: 'Verdana',
        source: 'local',
        ttfFiles: variants(
            'Verdana-400-normal.ttf',
            'Verdana-700-normal.ttf',
            'Verdana-400-italic.ttf',
            'Verdana-700-italic.ttf',
        ),
    }],
]);

// ── Internal fallback font (not a Quill font — used for Unicode coverage) ────

export const FALLBACK_FONT_ENTRY: FontRegistryEntry = {
    quillId: '__fallback__',
    pdfmakeName: 'NotoSansSymbols2',
    source: 'local',
    ttfFiles: variants('NotoSansSymbols2-400-normal.ttf'),
};

/** Look up a registry entry by Quill font ID */
export function getRegistryEntry(quillId: string): FontRegistryEntry | undefined {
    return FONT_REGISTRY.get(quillId);
}
